import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface UseSessionTimeoutOptions {
  enabled: boolean;
  warningMinutes?: number;
  timeoutMinutes?: number;
  onWarning?: () => void;
  onTimeout?: () => void;
}

/**
 * Hook to manage session timeout and auto-logout
 * Tracks user activity and shows warning before logging out
 */
export function useSessionTimeout({
  enabled = true,
  warningMinutes = 25, // Show warning at 25 minutes
  timeoutMinutes = 30, // Auto-logout at 30 minutes
  onWarning,
  onTimeout,
}: UseSessionTimeoutOptions) {
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  
  const warningTimeoutRef = useRef<NodeJS.Timeout>();
  const logoutTimeoutRef = useRef<NodeJS.Timeout>();
  const countdownIntervalRef = useRef<NodeJS.Timeout>();
  const lastActivityRef = useRef<number>(Date.now());

  const clearAllTimeouts = useCallback(() => {
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
  }, []);

  const handleLogout = useCallback(async () => {
    clearAllTimeouts();
    setShowWarning(false);
    
    try {
      await supabase.auth.signOut();
      toast.info('You have been logged out due to inactivity');
      navigate('/auth');
      onTimeout?.();
    } catch (error) {
      console.error('Logout error:', error);
    }
  }, [clearAllTimeouts, navigate, onTimeout]);

  const startCountdown = useCallback(() => {
    const warningTime = Date.now();
    setTimeRemaining((timeoutMinutes - warningMinutes) * 60);
    
    countdownIntervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - warningTime) / 1000);
      const remaining = Math.max(0, (timeoutMinutes - warningMinutes) * 60 - elapsed);
      setTimeRemaining(remaining);
      
      if (remaining === 0) {
        clearInterval(countdownIntervalRef.current);
      }
    }, 1000);
  }, [timeoutMinutes, warningMinutes]);

  const resetTimers = useCallback(() => {
    clearAllTimeouts();
    setShowWarning(false);
    lastActivityRef.current = Date.now();

    if (!enabled) return;

    // Set warning timeout
    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true);
      startCountdown();
      onWarning?.();
    }, warningMinutes * 60 * 1000);

    // Set logout timeout
    logoutTimeoutRef.current = setTimeout(() => {
      handleLogout();
    }, timeoutMinutes * 60 * 1000);
  }, [enabled, warningMinutes, timeoutMinutes, clearAllTimeouts, startCountdown, handleLogout, onWarning]);

  const extendSession = useCallback(() => {
    setShowWarning(false);
    resetTimers();
    toast.success('Session extended');
  }, [resetTimers]);

  useEffect(() => {
    if (!enabled) {
      clearAllTimeouts();
      return;
    }

    // Activity events to track
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityRef.current;
      
      // Only reset if more than 1 second has passed (avoid excessive resets)
      if (timeSinceLastActivity > 1000) {
        if (!showWarning) {
          resetTimers();
        }
      }
    };

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    // Initialize timers
    resetTimers();

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      clearAllTimeouts();
    };
  }, [enabled, resetTimers, clearAllTimeouts, showWarning]);

  return {
    showWarning,
    timeRemaining,
    extendSession,
    logout: handleLogout,
  };
}

/**
 * Format seconds into MM:SS format
 */
export function formatTimeRemaining(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}
